// src/pages/pentest/dashboard/Urls.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import HttpOutlinedIcon from '@mui/icons-material/HttpOutlined';
import DomainVerificationOutlinedIcon from '@mui/icons-material/DomainVerificationOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha, lighten } from '@mui/material/styles';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import Nav from './nav';
import { Doughnut, Bar } from 'react-chartjs-2';
import 'chart.js/auto';

const surfaceCardSx = {
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(PRIMARYCOLOR, 0.08),
  boxShadow: '0 18px 48px rgba(15, 23, 42, 0.06)',
  bgcolor: '#fff',
};

// ---- helpers ----
const statusChipColor = (code) => {
  if (code == null) return 'default';
  const c = Number(code);
  if (c >= 200 && c < 300) return 'success';
  if (c >= 300 && c < 400) return 'info';
  if (c >= 400 && c < 500) return 'warning';
  if (c >= 500) return 'error';
  return 'default';
};

const statusChipSx = (code) => {
  const c = Number(code);
  if (c >= 200 && c < 300) {
    return { bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32', borderColor: alpha('#2e7d32', 0.18) };
  }
  if (c >= 300 && c < 400) {
    return { bgcolor: alpha('#1976d2', 0.1), color: '#1976d2', borderColor: alpha('#1976d2', 0.18) };
  }
  if (c >= 400 && c < 500) {
    return { bgcolor: alpha('#ef6c00', 0.1), color: '#ef6c00', borderColor: alpha('#ef6c00', 0.18) };
  }
  if (c >= 500) {
    return { bgcolor: alpha('#d32f2f', 0.1), color: '#d32f2f', borderColor: alpha('#d32f2f', 0.18) };
  }
  return { bgcolor: alpha('#607d8b', 0.1), color: '#455a64', borderColor: alpha('#607d8b', 0.18) };
};

const formatBytes = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num < 0) return '—';
  if (num < 1024) return `${num} B`;
  if (num < 1024 ** 2) return `${(num / 1024).toFixed(1)} KB`;
  if (num < 1024 ** 3) return `${(num / 1024 ** 2).toFixed(1)} MB`;
  return `${(num / 1024 ** 3).toFixed(1)} GB`;
};

const getDomain = (url) => {
  try { return new URL(url).hostname; } catch { return '—'; }
};

const faviconFor = (url) => {
  try { return `${new URL(url).origin}/favicon.ico`; } catch { return undefined; }
};

const Urls = () => {
  const [urlsData, setUrlsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('url'); // url | title | status | domain
  const navigate = useNavigate();
  const { pentest_id } = useParams();

  // fetch
  const getUrls = (token) => {
    fetch(`${BASEURL}/pentest/urls?pentest_id=${pentest_id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.error && data?.message === 'Invalid token') navigate('/');
        else setUrlsData(Array.isArray(data?.urls) ? data.urls : []);
      })
      .catch((err) => console.error('Error fetching data:', err));
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) getUrls(token);
    else {
      console.error('No token found');
      navigate('/');
    }
  }, [navigate, pentest_id]);

  // enrich
  const rows = useMemo(() => {
    return urlsData.map((r, idx) => {
      const domain = getDomain(r.url);
      const status = r.status ?? null;
      const contentLen = r.content_length ?? null;
      return {
        id: idx + '-' + r.url,
        url: r.url,
        title: r.title || 'N/A',
        status,
        contentLen,
        domain,
        favicon: faviconFor(r.url),
      };
    });
  }, [urlsData]);

  // KPIs + charts
  const kpis = useMemo(() => {
    const total = rows.length;
    const domains = new Set(rows.map((r) => r.domain).filter(Boolean));
    const ok2xx = rows.filter((r) => Number(r.status) >= 200 && Number(r.status) < 300).length;
    const not2xx = total - ok2xx;
    return { total, uniqueDomains: domains.size, ok2xx, not2xx };
  }, [rows]);

  const statusBuckets = useMemo(() => {
    const buckets = { '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0, other: 0 };
    rows.forEach((r) => {
      const s = Number(r.status);
      if (!Number.isFinite(s)) buckets.other++;
      else if (s >= 200 && s < 300) buckets['2xx']++;
      else if (s >= 300 && s < 400) buckets['3xx']++;
      else if (s >= 400 && s < 500) buckets['4xx']++;
      else if (s >= 500) buckets['5xx']++;
      else buckets.other++;
    });
    return buckets;
  }, [rows]);

  const topDomains = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const d = r.domain || '—';
      map.set(d, (map.get(d) || 0) + 1);
    });
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [rows]);

  const statusDoughnut = useMemo(() => {
    const labels = Object.keys(statusBuckets);
    const data = Object.values(statusBuckets);
    const colors = [
      'rgba(76,175,80,0.7)',   // 2xx
      'rgba(25,118,210,0.7)',  // 3xx
      'rgba(255,152,0,0.7)',   // 4xx
      'rgba(244,67,54,0.7)',   // 5xx
      'rgba(158,158,158,0.7)', // other
    ];
    return { labels, datasets: [{ data, backgroundColor: colors, borderWidth: 0 }] };
  }, [statusBuckets]);

  const domainBarData = useMemo(() => {
    const labels = topDomains.map(([d]) => d);
    const data = topDomains.map(([, c]) => c);
    return {
      labels,
      datasets: [
        {
          label: 'URLs',
          data,
          backgroundColor: lighten(PRIMARYCOLOR, 0.7),
          borderColor: lighten(PRIMARYCOLOR, 0.7),
          borderWidth: 1,
        },
      ],
    };
  }, [topDomains]);

  // filtering
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter((r) => {
      if (searchCategory === 'url') return (r.url || '').toLowerCase().includes(q);
      if (searchCategory === 'title') return (r.title || '').toLowerCase().includes(q);
      if (searchCategory === 'status') return String(r.status ?? '').toLowerCase().includes(q);
      if (searchCategory === 'domain') return (r.domain || '').toLowerCase().includes(q);
      return true;
    });
  }, [rows, searchCategory, searchTerm]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', bgcolor: lighten(PRIMARYCOLOR, 0.985) }}>
      <Nav />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        {/* ---------- Header & KPIs ---------- */}
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
            <Card sx={{ ...surfaceCardSx, p: 2.6, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                URLs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Aggregated list of discovered URLs with titles, status codes, and content lengths.
              </Typography>
            </Card>

            <Card sx={{ ...surfaceCardSx, p: 2.2, minWidth: 180 }}>
              <LanguageOutlinedIcon sx={{ color: PRIMARYCOLOR, fontSize: 22, mb: 1 }} />
              <Typography variant="overline" color="text.secondary">Total URLs</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>{kpis.total}</Typography>
            </Card>
            <Card sx={{ ...surfaceCardSx, p: 2.2, minWidth: 180 }}>
              <TravelExploreOutlinedIcon sx={{ color: PRIMARYCOLOR, fontSize: 22, mb: 1 }} />
              <Typography variant="overline" color="text.secondary">Unique Domains</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>{kpis.uniqueDomains}</Typography>
            </Card>
            <Card sx={{ ...surfaceCardSx, p: 2.2, minWidth: 180 }}>
              <DomainVerificationOutlinedIcon sx={{ color: '#2e7d32', fontSize: 22, mb: 1 }} />
              <Typography variant="overline" color="text.secondary">OK (2xx)</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>{kpis.ok2xx}</Typography>
            </Card>
            <Card sx={{ ...surfaceCardSx, p: 2.2, minWidth: 180 }}>
              <HttpOutlinedIcon sx={{ color: '#ef6c00', fontSize: 22, mb: 1 }} />
              <Typography variant="overline" color="text.secondary">Non-2xx</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>{kpis.not2xx}</Typography>
            </Card>
          </Stack>

          {/* Charts */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Card sx={{ ...surfaceCardSx, p: 2.4, flex: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Status Classes</Typography>
                <Typography variant="caption" color="text.secondary">
                  Distribution of 2xx/3xx/4xx/5xx/other
                </Typography>
              </Stack>
              {rows.length ? (
                <Box sx={{ height: 260 }}>
                  <Doughnut
                    data={statusDoughnut}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { position: 'bottom' } },
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No data yet.</Typography>
              )}
            </Card>

            <Card sx={{ ...surfaceCardSx, p: 2.4, flex: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Top Domains</Typography>
                <Typography variant="caption" color="text.secondary">
                  Top {topDomains.length || 0} by URL count
                </Typography>
              </Stack>
              {topDomains.length ? (
                <Box sx={{ height: 260 }}>
                  <Bar
                    data={domainBarData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No domain data yet.</Typography>
              )}
            </Card>
          </Stack>

          <Divider />
        </Stack>

        {/* ---------- Filters ---------- */}
        <Card sx={{ ...surfaceCardSx, p: 2.4, mt: 0.5 }}>
          <Stack
            direction={{ xs: 'column', xl: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', xl: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                URL Inventory
              </Typography>
              <Chip
                size="small"
                label={`${filteredRows.length}/${rows.length || 0} shown`}
                sx={{ ml: 1, fontWeight: 700 }}
                variant="outlined"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Filter By</InputLabel>
                <Select
                  value={searchCategory}
                  label="Filter By"
                  onChange={(e) => setSearchCategory(e.target.value)}
                >
                  <MenuItem value="url">URL</MenuItem>
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                  <MenuItem value="domain">Domain</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search URLs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: { xs: '100%', md: 280 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="outlined" onClick={() => setSearchTerm('')} sx={{ borderRadius: 999 }}>
                Clear
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* ---------- Table ---------- */}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: alpha(PRIMARYCOLOR, 0.08),
              boxShadow: 'none',
              overflow: 'hidden',
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{ '& .MuiTableCell-root': { borderColor: alpha(PRIMARYCOLOR, 0.06) } }}
            >
              <TableHead>
                <TableRow sx={{ '& th': { backgroundColor: PRIMARYCOLOR, color: '#fff', fontWeight: 800, borderColor: PRIMARYCOLOR } }}>
                  <TableCell sx={{ color: 'white', fontWeight: 800 }}>URL</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800 }}>Title</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800 }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800 }}>Content Length</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 90 }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredRows.length ? (
                  filteredRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: alpha(PRIMARYCOLOR, 0.015) },
                        '&:hover': { backgroundColor: alpha(PRIMARYCOLOR, 0.05) },
                        '& td': { py: 1.6 },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="flex-start">
                          <Avatar
                            src={row.favicon}
                            imgProps={{ referrerPolicy: 'no-referrer' }}
                            sx={{
                              width: 30,
                              height: 30,
                              bgcolor: alpha(PRIMARYCOLOR, 0.12),
                              color: PRIMARYCOLOR,
                              fontWeight: 800,
                            }}
                          >
                            {(row.domain || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: '#10243E' }} noWrap title={row.url}>
                              {row.url}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                              {row.domain}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography noWrap title={row.title}>{row.title}</Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status ?? 'N/A'}
                          color={statusChipColor(row.status)}
                          variant="outlined"
                          sx={{ fontWeight: 800, ...statusChipSx(row.status) }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography>{formatBytes(row.contentLen)}</Typography>
                      </TableCell>

                      <TableCell>
                        <Tooltip title="Open URL">
                          <IconButton
                            size="small"
                            component="a"
                            href={row.url}
                            target="_blank"
                            rel="noreferrer"
                            sx={{
                              border: '1px solid',
                              borderColor: alpha(PRIMARYCOLOR, 0.12),
                              bgcolor: alpha(PRIMARYCOLOR, 0.04),
                            }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={5}>
                      <Typography>No data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
};

export default Urls;

